import type { ScanCandidate, ScpData } from "@hobbyshopify/backend-client";

/** A candidate as the review list reads it. */
export type CandidateLine = {
  /** The catalog id, what picking sends; null for a row the source gave no id. */
  productId: string | null;
  /** "Victor Wembanyama #136". */
  title: string;
  /** "2023 Topps Chrome Basketball · Refractor". */
  subtitle: string;
  /** What tells look-alikes apart on a chip: the parallel, else the title. */
  label: string;
  /** The parallel alone, "" for a base card. */
  parallel: string;
};

const idOf = (product: ScpData): string | null => {
  const id = product.id;
  if (typeof id !== "string" && typeof id !== "number") return null;
  const text = String(id).trim();
  return text === "" ? null : text;
};

/**
 * SCP writes the parallel in square brackets ("Jason Kidd [Gold Refractor]
 * #35") and the set in `console-name`; the two are shown apart so a list of
 * look-alikes differs where it matters.
 */
export function describeCandidate(product: ScpData): CandidateLine {
  const name = String(product["product-name"] ?? "").trim();
  const parallel = name.match(/\[([^\]]+)\]/)?.[1]?.trim() ?? "";
  const title = name
    .replace(/\s*\[[^\]]*\]\s*/, " ")
    .replace(/\s+/g, " ")
    .trim();
  const set = String(product["console-name"] ?? "").trim();
  const shown = title || name || "Unnamed product";
  return {
    productId: idOf(product),
    title: shown,
    subtitle: [set, parallel].filter(Boolean).join(" · "),
    label: parallel || shown,
    parallel,
  };
}

/** The candidate the scan is priced from, by the pick's catalog id. */
export function selectedProductId(scpData: ScpData | null): string | null {
  return scpData ? idOf(scpData) : null;
}

/**
 * Whether the list is worth a reviewer's eye: more than one product to choose
 * between, or one the lookup declined to pick.
 */
export function hasChoice(candidates: readonly ScanCandidate[], scpData: ScpData | null): boolean {
  return candidates.length > 1 || (candidates.length === 1 && selectedProductId(scpData) === null);
}

/**
 * Whether the pick is still the scanner's own (rules or the model): the lookup
 * ranks its pick 0, and a reviewer's pick is any other candidate.
 */
export function isScannerPick(candidates: readonly ScanCandidate[], scpData: ScpData | null): boolean {
  const pick = selectedProductId(scpData);
  const first = candidates.find((candidate) => candidate.rank === 0);
  return pick !== null && (first === undefined || idOf(first.product) === pick);
}
